import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Box, IconButton, TextField, Link, Modal } from '@mui/material';
import { Close, Loader } from '../../assets/images/index';
import CustomTabs from '../custom-tabs';
import MathExpressionNew from '../common/mathjax';
import ExerciseSolution from '../exercise-solution';
import ExerciseQuestionList from "../exercise-question-list";
import Dropdown from "../dropdown/index";
import Button from '../button';
import { ChatService } from "../../services/chat/chat.services";
import { ExerciseQuestionsService } from "../../services/exercise-questions/exercise-questions.service";
import { SearchExerciseService } from "../../services/search-exercise/search-exercise.service";
import { NewExerciseService } from "../../services/new-exercises/new-exercises.service";
import { scopeCSS } from "../../utils/scopeCSS";
import { hebrewLetters } from '../../configs/config';
import { useLessonStore } from '../../utils/lessonStore';
import { showSuccessToast, showErrorToast } from '../Toaster';
import './exercise-details-drawer.scss';

interface ExerciseDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  exerciseId: number | null;
  mode?: 'preview' | 'edit';
  onUpdateSuccess?: () => void;
}

const ExerciseDetailsDrawer: React.FC<ExerciseDetailsDrawerProps> = ({ 
  open, 
  onClose, 
  exerciseId, 
  mode = 'preview',
  onUpdateSuccess
}) => {
  const { t } = useTranslation();
  const chatService = new ChatService();
  const exerciseQuestionsService = new ExerciseQuestionsService();
  const searchExerciseService = new SearchExerciseService();
  const newExerciseService = new NewExerciseService();

  const {
    topicOptions: storeTopicOptions,
    isTopicsLoading,
    fetchTopics: storeFetchTopics,
  } = useLessonStore();

  const [exerciseData, setExerciseData] = useState<any>(null);
  const [exerciseContent, setExerciseContent] = useState<string>("");
  const [exerciseSolution, setExerciseSolution] = useState<string>("");
  const [exerciseQuestions, setExerciseQuestions] = useState<any[]>([]);
  const [exerciseCharts, setExerciseCharts] = useState<any[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [openChartModal, setOpenChartModal] = useState(false);
  const [chartImage, setChartImage] = useState<string | null>(null);

  // Edit states
  const [editLevel, setEditLevel] = useState('');
  const [editTopic, setEditTopic] = useState('');
  const [editDifficulty, setEditDifficulty] = useState('');
  const [levelOptions, setLevelOptions] = useState([{ value: '', label: t('all') }]);
  const [difficultyOptions, setDifficultyOptions] = useState([{ value: '', label: t('all') }]);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  const topicOptions = useMemo(() => [{ value: '', label: t('all') }, ...storeTopicOptions], [storeTopicOptions, t]);

  useEffect(() => {
    if (open && exerciseId) {
      fetchData(exerciseId);
      if (mode === 'edit') {
        fetchOptions();
        storeFetchTopics(searchExerciseService);
      }
    } else {
      setExerciseContent("");
      setExerciseSolution("");
      setExerciseQuestions([]);
      setExerciseCharts([]);
      setActiveTabIndex(0);
      setIsEditingName(false);
    }
  }, [open, exerciseId, mode]);

  const fetchOptions = async () => {
    try {
      const [levels, difficulties] = await Promise.all([
        searchExerciseService.getLevels(),
        searchExerciseService.getDifficulties()
      ]);
      setLevelOptions([{ value: '', label: t('all') }, ...levels.map((l: any) => ({ value: l.id.toString(), label: l.name }))]);
      setDifficultyOptions([{ value: '', label: t('all') }, ...difficulties.map((d: any) => ({ value: d.id.toString(), label: d.name }))]);
    } catch (error) {
      console.error("Failed to fetch options", error);
    }
  };

  const fetchData = async (id: number) => {
    setLoading(true);
    try {
      const contentRes = await chatService.GETEXERCISECONTENT(id);
      setExerciseData(contentRes);
      setExerciseContent(await scopeCSS(contentRes.exercise_content));
      setExerciseSolution(await scopeCSS(contentRes.solution_content));
      setEditNameValue(contentRes.filename?.replace('.docx', '') || '');
      
      setEditLevel(contentRes.lu_level?.id?.toString() || '');
      setEditTopic(contentRes.lu_topic?.id?.toString() || '');
      setEditDifficulty(contentRes.lu_difficulty?.id?.toString() || '');

      const questionsRes = await exerciseQuestionsService.fetchExerciseQuestions(id);
      const safeQuestions = Array.isArray(questionsRes) ? questionsRes : [];
      setExerciseQuestions(safeQuestions);

      const charts = safeQuestions
        .filter((q: any) => q.chart_image_data)
        .map((q: any) => ({
          question_id: q.id,
          chart_image_base64: q.chart_image_data
        }));
      setExerciseCharts(charts);
    } catch (error) {
      console.error("Failed to fetch exercise details:", error);
      setExerciseQuestions([]);
      setExerciseCharts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (payload: any, shouldCloseOnSuccess: boolean = false) => {
    if (!exerciseId) return;
    setIsUpdating(true);
    try {
      await searchExerciseService.updateExercise(exerciseId, payload);
      onUpdateSuccess?.();
      if (shouldCloseOnSuccess) {
        showSuccessToast(t('exerciseUpdatedSuccessfully'));
        onClose();
      }
    } catch (error) {
      console.error("Update failed", error);
      showErrorToast(t('exerciseUpdateFailed'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRename = async () => {
    if (!exerciseId || !editNameValue.trim() || !exerciseData) return;
    setIsRenaming(true);
    try {
      const oldFilename = exerciseData.filename;
      const newFilename = `${editNameValue.trim()}.docx`;
      const res = await newExerciseService.renameExerciseName(oldFilename, newFilename);
      if (res) {
        setIsEditingName(false);
        setExerciseData({ ...exerciseData, filename: newFilename });
        onUpdateSuccess?.();
      }
    } catch (error) {
      console.error("Rename failed", error);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleOpenChart = (img: string) => {
    setChartImage(img);
    setOpenChartModal(true);
  };
  const handleCloseChart = () => setOpenChartModal(false);

  const tabs = [
    { 
      label: t('exerciseDisplay'), 
      content: (
        <div className="exercise-preview-tab">
          {mode === 'edit' && (
            <div className='excercise_display_header_container'>
              <h4>{t('levelAndTypeExercise')}</h4>
              <div className='excercise_display_header'>
                <Dropdown label={t('level')} value={editLevel} options={levelOptions} onChange={(val) => setEditLevel(val.toString())} />
                <Dropdown label={t('topic')} value={editTopic} options={topicOptions} loading={isTopicsLoading} onChange={(val) => setEditTopic(val.toString())} />
                <Dropdown label={t('difficulty')} value={editDifficulty} options={difficultyOptions} onChange={(val) => setEditDifficulty(val.toString())} />
              </div>
            </div>
          )}
          <div className='excercise_display_content_container'>
            <div className='excercise_display_newExercise'>
              <h6>{t('exercise')}</h6>
              {exerciseContent ? (
                <div id="scoped-db-content">
                  <MathExpressionNew content={exerciseContent} />
                </div>
              ) : (
                <div className="loaderBlock"><img src={Loader} alt="Loader" /></div>
              )}
            </div>
            <div className='excercise_display_excerciseList'>
              <h6>{t('questions')}</h6>
              {exerciseQuestions.length > 0 ? (
                <ExerciseQuestionList exerciseQuestions={exerciseQuestions} hebrewLetters={hebrewLetters} isViewOnly={true} showChartLink={true} />
              ) : (
                <div className="loaderBlock">
                  {loading ? (
                    <img src={Loader} alt="Loader" />
                  ) : (
                    <span>{t('noQuestionsFound')}</span>
                  )}
                </div>
              )}
            </div>
          </div>
          {mode === 'edit' && (
            <div className='drawer_footer'>
              <Button text={t('showExerciseSolution')} color='lightBlue' onClick={() => setActiveTabIndex(1)}/>
              <Button text={t('exerciseApproval')} color='blue' loader={isUpdating} onClick={() => handleUpdate({
                level_id: parseInt(editLevel),
                topic_id: parseInt(editTopic),
                difficulty_id: parseInt(editDifficulty),
                is_exercise_approved: true
              }, true)} />
            </div>
          )}
        </div>
      ) 
    },
    { 
      label: t('exerciseSolution'), 
      content: (
        <ExerciseSolution
          exerciseSolution={exerciseSolution}
          exerciseCharts={exerciseCharts}
          exerciseQuestions={exerciseQuestions}
          isUpdating={isUpdating}
          selectedExercise={{ id: exerciseId }}
          t={t}
          onUpdate={() => handleUpdate({ is_solution_approved: true })}
          onOpenChart={handleOpenChart} 
          updateChart={() => {}}
          isReadOnly={mode === 'preview'}
        />
      ) 
    },
  ];

  return (
    <Drawer anchor="right" open={open} onClose={onClose} transitionDuration={0} className="exercise-details-drawer-root">
      <Box className="drawer_content exercise-details-drawer">
        <div className='drawer_header'>
          <div className="exercise-details">
            <span>{t('exerciseId')}: {exerciseId}</span>
            {mode === 'edit' ? (
              <div className="exercise_name_edit_container">
                {isEditingName ? (
                  <>
                    <span>{t('exerciseName')}:</span>
                    <TextField size="small" value={editNameValue} onChange={(e) => setEditNameValue(e.target.value)} disabled={isRenaming} />
                    {isRenaming ? <img src={Loader} alt="loading" className="rename_loader" /> : (
                      <>
                        <Link component="button" variant="caption" onClick={handleRename} className="rename_name_link">{t('save')}</Link>
                        <Link component="button" variant="caption" onClick={() => setIsEditingName(false)} className="rename_name_link">{t('cancel')}</Link>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <span>{t('exerciseName')}: {exerciseData?.filename?.replace('.docx', '')}</span>
                    <Link component="button" variant="caption" onClick={() => setIsEditingName(true)} className="rename_name_link">{t('renameName')}</Link>
                  </>
                )}
              </div>
            ) : (
              <span>{t('exerciseName')}: {exerciseData?.filename?.replace('.docx', '')}</span>
            )}
          </div>
          <button onClick={onClose}><img src={Close} alt="Close" /></button>
        </div>
        <div className='drawer_body'>
          {loading ? (
            <div className="drawer-loader"><img src={Loader} alt="Loading" /></div>
          ) : (
            <CustomTabs tabs={tabs} activeIndex={activeTabIndex} setActiveIndex={setActiveTabIndex} />
          )}
        </div>
      </Box>
      <Modal open={openChartModal} onClose={handleCloseChart} className="chart-modal-root">
        <Box className="chart-modal-box">
          <button
            className="chart-modal-close"
            onClick={handleCloseChart}
            aria-label="Close"
          >
            ×
          </button>
          <img
            src={chartImage ? (chartImage.startsWith('data:') ? chartImage : `data:image/png;base64,${chartImage}`) : ''}
            alt="Chart"
            className="chart-modal-img"
          />
        </Box>
      </Modal>
    </Drawer>
  );
};

export default ExerciseDetailsDrawer;
